(ns frontend.view.modals.ai-deal-one-pager.view
  (:require
   [common.localization :as loc]
   [common.schema.entities.integration.ai-options :as ai-options]
   [frontend.state.current-session :as session]
   [frontend.ui.ai-summary-configuration.editor :as configuration-editor]
   [frontend.view.modals.ai-deal-one-pager.event :as event]
   [frontend.view.modals.ai-deal-one-pager.model :as model]
   [frontend.view.modals.ai-deal-one-pager.sub :as sub]
   [frontend.view.modals.events :as modal-events]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.components.modal.style :as modal-style]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :ai-deal-one-pager-modal)

(defn open!
  [deal-id pipeline-id]
  (rf/dispatch [::event/open deal-id pipeline-id])
  (modal-events/open-modal! modal-key {:deal-id deal-id}))

(defn- status-copy-key
  [status]
  (case status
    :ai.status/pending :deal-one-pager/status-pending
    :ai.status/processing :deal-one-pager/status-processing
    :ai.status/completed :deal-one-pager/status-completed
    :ai.status/failed :deal-one-pager/status-failed
    :ai.status/token-exhausted :deal-one-pager/status-token-exhausted
    :deal-one-pager/status-starting))

(defn- error-copy-key
  [error-category]
  (case error-category
    :service-unavailable :deal-one-pager/service-unavailable
    :rate-limited :deal-one-pager/rate-limited
    :access-denied :deal-one-pager/access-denied
    :deal-one-pager/request-failed))

(defn- active-body
  [status]
  [:div
   {:class "flex flex-col items-center gap-5 py-8 text-center"}
   [:i
    {:class "fal fa-spinner fa-pulse fa-3x text-primary-regular"}]
   [:div
    {:class "flex flex-col gap-2"}
    [:div
     {:class "text-lg font-semibold text-base-light-text"}
     (loc/tr (status-copy-key status))]
    [:div
     {:class "text-sm text-base-light-text-paler"}
     (loc/tr :deal-one-pager/processing-hint)]]])

(defn- completed-body
  [generation]
  (let [{:keys [filename renderer]} (model/output-attachment generation)]
    [:div
     {:class "flex flex-col items-center gap-5 py-8 text-center"}
     [:div
      {:class "flex h-14 w-14 items-center justify-center rounded-full bg-success-lighter"}
      [:i
       {:class "fal fa-check fa-2x text-success-regular"}]]
     [:div
      {:class "flex flex-col gap-2"}
      [:div
       {:class "text-lg font-semibold text-base-light-text"}
       (loc/tr :deal-one-pager/status-completed)]
      (when filename
        [:div
         {:class "text-sm text-base-light-text-paler"}
         filename])
      (when (= renderer :pdf-fallback)
        [:div
         {:class "text-sm text-base-light-text-paler"}
         (loc/tr :deal-one-pager/pdf-fallback-hint)])]]))

(defn- failure-body
  [copy-key]
  [:div
   {:class "flex flex-col items-center gap-5 py-8 text-center"}
   [:div
    {:class "flex h-14 w-14 items-center justify-center rounded-full bg-warning-lighter"}
    [:i
     {:class "fal fa-exclamation-triangle fa-2x text-warning-regular"}]]
   [:div
    {:class "max-w-lg text-base text-base-light-text"}
    (loc/tr copy-key)]])

(defn- clear-document!
  [field]
  (case field
    :style-guideline-document
    (do
      (rf/dispatch [::event/update-draft :style-guideline-document-id nil])
      (rf/dispatch [::event/update-draft
                    :style-guideline-document-filename nil]))

    :document-template
    (do
      (rf/dispatch [::event/update-draft :document-template-id nil])
      (rf/dispatch [::event/update-draft :document-template-filename nil]))

    nil))

(defn- configuration-editor-body
  [{:keys [configuration-detail draft copy-source-name save-state
           uploading-field create-mode?]}]
  (let [uploading? (some? uploading-field)]
    [:div
     {:class "flex flex-col gap-5"}
     [:div
      {:class "rounded bg-base-lighter p-3 text-sm text-base-light-text-paler"}
      (loc/tr (if create-mode?
                :deal-one-pager/create-configuration-hint
                :deal-one-pager/unsaved-changes-hint))]
     (when copy-source-name
       [:div
        {:class "rounded border border-primary-light-border bg-primary-lighter p-3 text-sm text-base-light-text"}
        (loc/tr :deal-one-pager/copy-name-required)])
     [configuration-editor/view
      {:draft draft
       :uploading-field uploading-field
       :on-change (fn [field value]
                    (rf/dispatch [::event/update-draft field value]))
       :on-toggle-source (fn [source selected?]
                           (rf/dispatch [::event/toggle-source source selected?]))
       :on-upload (fn [field file]
                    (rf/dispatch [::event/upload-document field file]))
       :on-clear clear-document!}]
     (when (contains? #{:failed :upload-failed :invalid-template} save-state)
       [:div
        {:class "text-sm text-danger-light-text"}
        (loc/tr (case save-state
                  :upload-failed :deal-one-pager/document-upload-failed
                  :invalid-template :deal-one-pager/template-file-invalid
                  :deal-one-pager/configuration-save-failed))])
     (when (= :saved save-state)
       [:div
        {:class "text-sm text-success-regular"}
        (loc/tr :deal-one-pager/configuration-saved)])
     (when-not (:owned-by-requester? configuration-detail)
       [:div
        {:class "text-xs text-base-light-text-paler"}
        (loc/tr :deal-one-pager/shared-configuration-hint)])
     (when uploading?
       [:div
        {:class "text-xs text-base-light-text-paler"}
        (loc/tr :deal-one-pager/upload-before-generation)])]))

(defn- selection-body
  [{:keys [library-state configurations selected-configuration-id
           configuration-detail-state]
    :as state}]
  (cond
    (= :loading library-state)
    [active-body nil]

    (= :failed library-state)
    [failure-body :deal-one-pager/configuration-load-failed]

    (model/create-mode? state)
    [configuration-editor-body state]

    (empty? configurations)
    [:div
     {:class "flex flex-col items-center gap-3 py-8 text-center"}
     [:i
      {:class "fal fa-books fa-3x text-base-light-text-paler"}]
     [:div
      {:class "text-lg font-semibold text-base-light-text"}
      (loc/tr :deal-one-pager/no-configurations)]
     [:div
      {:class "max-w-lg text-sm text-base-light-text-paler"}
      (loc/tr :deal-one-pager/no-configurations-hint)]]

    :else
    (let [options (mapv (fn [configuration]
                          {:value (:id configuration)
                           :label (:configuration-name configuration)})
                        configurations)]
      [:div
       {:class "flex flex-col gap-5 py-4"}
       [:div
        {:class "text-sm text-base-light-text-paler"}
        (loc/tr :deal-one-pager/select-configuration-hint)]
       [labelled-input/view
        {:editor-type :editor.type/select
         :label (loc/tr :deal-one-pager/configuration)
         :mandatory? true
         :options options
         :value selected-configuration-id
         :placeholder (loc/tr :deal-one-pager/select-configuration)
         :on-change (fn [configuration-id _option]
                      (when configuration-id
                        (rf/dispatch [::event/select-configuration
                                      configuration-id])))}]
       (case configuration-detail-state
         :loading [active-body nil]
         :failed [failure-body :deal-one-pager/configuration-detail-load-failed]
         :ready [configuration-editor-body state]
         nil)])))

(defn- body
  [{:keys [request-state generation error-category] :as state}]
  (let [status (:status generation)]
    (cond
      (contains? #{:loading :starting nil} request-state)
      [active-body nil]

      (contains? #{:load-failed :start-failed :poll-failed} request-state)
      [failure-body (error-copy-key error-category)]

      (model/generation-active? generation)
      [active-body status]

      (model/generation-completed? generation)
      (if (model/output-attachment generation)
        [completed-body generation]
        [failure-body :deal-one-pager/output-unavailable])

      (= ai-options/STATUS-TOKEN-EXHAUSTED status)
      [failure-body :deal-one-pager/status-token-exhausted]

      (and (= ai-options/STATUS-FAILED status)
           (= :ai.failure/template (:failure-category generation)))
      [failure-body :deal-one-pager/status-template-failed]

      (= ai-options/STATUS-FAILED status)
      [failure-body :deal-one-pager/status-failed]

      (nil? generation)
      [selection-body state]

      :else
      [failure-body :deal-one-pager/request-failed])))

(defn- footer
  [{:keys [close-modal! request-state generation configuration-detail
           save-state uploading-field]
    :as state}]
  (let [output (model/output-attachment generation)
        retry? (or (contains? #{:load-failed :start-failed :poll-failed} request-state)
                   (model/generation-failed? generation)
                   (and (model/generation-completed? generation)
                        (nil? output)))
        generate-again? (and (model/generation-completed? generation)
                             (some? output))
        select-and-generate? (and (nil? generation)
                                  (= :ready request-state)
                                  (seq (:configurations state)))
        editing? (and select-and-generate?
                      (= :ready (:configuration-detail-state state)))
        copy-mode? (model/copy-mode? state)
        create-mode? (model/create-mode? state)
        create-available? (and (nil? generation)
                               (= :ready request-state)
                               (= :ready (:library-state state))
                               (not create-mode?)
                               (empty? (:configurations state))
                               (not (session/is-read-only-user?)))
        saving? (= :saving save-state)
        blocked? (or saving? (some? uploading-field))]
    [modal/footer-children-left-and-right
     [[button/secondary-hollow
       {:on-click (fn [_]
                    (rf/dispatch [::event/close close-modal!]))}
       (loc/tr :close)]]
     [(when retry?
        [button/primary-solid
         {:on-click (fn [_]
                      (rf/dispatch [::event/retry]))}
         (loc/tr :deal-one-pager/retry)])
      (when generate-again?
        [button/secondary-hollow
         {:on-click (fn [_]
                      (rf/dispatch [::event/prepare-new]))}
         (loc/tr :deal-one-pager/generate-again)])
      (when create-available?
        [button/primary-solid
         {:on-click (fn [_]
                      (rf/dispatch [::event/prepare-create]))}
         [:i
          {:class "fal fa-plus"}]
         (loc/tr :deal-one-pager/create-configuration)])
      (when create-mode?
        [:<>
         [button/secondary-hollow
          {:disabled? blocked?
           :on-click (fn [_]
                       (rf/dispatch [::event/cancel-create]))}
          (loc/tr :cancel)]
         [button/primary-solid
          {:disabled? (or blocked?
                          (not (model/draft-valid? (:draft state))))
           :on-click (fn [_]
                       (rf/dispatch [::event/save-new]))}
          (loc/tr :deal-one-pager/create-configuration)]])
      (when select-and-generate?
        [:<>
         (when (and editing?
                    (not copy-mode?)
                    (:owned-by-requester? configuration-detail))
           [button/secondary-hollow
            {:disabled? (or blocked?
                            (not (model/draft-dirty? state))
                            (not (model/draft-valid? (:draft state))))
             :on-click (fn [_]
                         (rf/dispatch [::event/save-current]))}
            (loc/tr :deal-one-pager/update-configuration)])
         (when (and editing? (not copy-mode?))
           [button/secondary-hollow
            {:disabled? blocked?
             :on-click (fn [_]
                         (rf/dispatch [::event/prepare-save-as-new]))}
            (loc/tr :deal-one-pager/save-as-new)])
         (when (and editing? copy-mode?)
           [:<>
            [button/secondary-hollow
             {:disabled? blocked?
              :on-click (fn [_]
                          (rf/dispatch [::event/cancel-save-as-new]))}
             (loc/tr :cancel)]
            [button/secondary-hollow
             {:disabled? (or blocked?
                             (not (model/draft-valid? (:draft state)))
                             (not (model/copy-name-valid? state)))
              :on-click (fn [_]
                          (rf/dispatch [::event/save-as-new]))}
             (loc/tr :deal-one-pager/create-copy)]])
         [button/primary-solid
          {:disabled? (or blocked?
                          copy-mode?
                          (not (model/generation-ready? state)))
           :on-click (fn [_]
                       (rf/dispatch [::event/start]))}
          (loc/tr :deal-one-pager/generate)]])
      (when output
        [button/primary-solid
         {:on-click (fn [_]
                      (rf/dispatch [::event/download (:id output)]))}
         [:i
          {:class "fal fa-cloud-download"}]
         (loc/tr :deal-one-pager/download)])]]))

(defn view
  [{:keys [shown? close-modal! topmost?]}]
  (let [state @(rf/subscribe [::sub/state])]
    [modal/view
     {:visible? (and shown? topmost?)
      :hide! (fn []
               (rf/dispatch [::event/close close-modal!]))
      :close-button? true
      :size ::modal-style/lg
      :backdrop "static"}
     (loc/tr :deal-one-pager/title)
     [body state]
     [footer (assoc state :close-modal! close-modal!)]]))
